import { withNamespaces } from "react-i18next";
import { Row, Col, CardBody, Card, CardTitle } from "reactstrap";
import { useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import DashboardService from "../../../../../apis/DashboardService";

import Widget from "../../components/Widget";
import { DonutChart, LineChart } from "../../components/ApexChart";
import Knob from "../../components/Knob";
import Filtering from "../../components/Filtering";

import {
    TasksStatuses
} from "src/modules/data-protection/constants"
import { toast } from "react-toastify";
import { Assets_STATUS_LIST } from "src/modules/data-protection/constants";

const TabProcessingActivityContent = (props) => {
    const {
        t
    } = props;


	const [ totalWidget, setTotalWidget ] = useState(null);
	const [ assetsByDateList, setAssetsByDateList ] = useState([]);
	const [ assignedToMe, setAssignedToMe ] = useState([]);
	const [ myHardware, setMyHardware ] = useState([]);
	const [ mySoftware, setMySoftware] = useState([]);
	const [ myTasks, setMyTasks] = useState([]);

	const [filters, setFilters] = useState({});

	const handleFilterOnChange = useCallback((newFilters) => {
		setFilters((currentState) => ({...currentState, ...newFilters}));
	}, []);

    const handleFetchDashboardQuery = useQuery({
		queryKey: ["data-protection-admin-processing-activities-dashboard", filters],
		queryFn: async () => {
		  const service = DashboardService.getInstance();
	
		  return await service.list({
			'createdAt[from]'	:	filters['createdAt[from]'],
			'createdAt[to]'		:	filters['createdAt[to]'],
			questionnaire_name	:	"processing_activity",
		  });
		},
		cacheTime: 0,
        refetchOnWindowFocus: false,
		onError: (error) => {
		  if (process.env.NODE_ENV === 'development') console.error(error);
	
		  toast(t("An error occurred while fetching dashboard data."), {
			type: "error",
		  });
		}
	});

	useEffect(() => {
		const { data } = handleFetchDashboardQuery;
		if(data){
			const {
				assetsByDate	: 	gotAssetsByDate,
				assignedToMe 	: 	gotAssignedToMe,
				myHardware 		:	gotHardware,
				mySoftware 		:	gotSoftware,
				totalCases		: 	gotTotalCases,
				myTasks			:	gotTasks
			} = handleFetchDashboardQuery.data;

			const hardwares = [];
			const softwares = [];
			const tasks = [];

			/**
			 * hardwares
			 */
			for (const item in (gotHardware || [])) {
				const statusItem = Assets_STATUS_LIST[item];
				hardwares.push({
					color	: 	statusItem?.backgroundColor,
					amount	: 	gotHardware[item].amount,
					title	: 	statusItem?.title,
					percent	:	gotHardware[item].percent,
				});
			}
			/**-------------------------------- */

			/**
			 * softwares
			 */
			for (const item in (gotSoftware || [])) {
                const statusItem = Assets_STATUS_LIST[item];
				softwares.push({
					color	: 	statusItem?.backgroundColor,
					amount	: 	gotSoftware[item].amount,
					title	: 	statusItem?.title,
					percent	:	gotSoftware[item].percent,
				});
			}
			/**-------------------------------- */

			/**
			 * tasks
			 */
			for (const item in (gotTasks || [])) {
				if(!['DELETED'].includes(item)){
					tasks.push({
						color	: 	gotTasks[item]?.color,
						amount	: 	gotTasks[item]?.amount,
                        title   :   t(TasksStatuses[item]?.title),
						percent	:	gotTasks[item]?.percent,
					});
				}
			}
			/**-------------------------------- */

			setTotalWidget({
				amount	:	gotTotalCases,
				percent	: 	100,
				title	: 	'Total',
				icon	: 	'ri-stack-line',
				displayChart	: false,
			});
			setAssetsByDateList(gotAssetsByDate);
			setAssignedToMe(gotAssignedToMe);
			setMyHardware(hardwares);
			setMySoftware(softwares);
			setMyTasks(tasks);
			
		}
	}, [handleFetchDashboardQuery.data, t]);

    const renderBody = () => {
        if (handleFetchDashboardQuery.isFetching) {
            return (
                <Row className="mt-4">
                    <Col sm="12" md="8">
                        <div
                            className="dt-field dt-skeleton"
                            style={{ width: '100%', height: '420px' }}
                        />
                    </Col>

                    <Col sm="12" md="4">
                        <div
                            className="dt-field dt-skeleton"
                            style={{ width: '100%', height: '120px' }}
                        />

                        <div
                            className="dt-field dt-skeleton mt-3"
                            style={{ width: '100%', height: '280px' }}
                        />
                    </Col>

                    <Col sm="12" md="4" className="mt-3">
                        <div
                            className="dt-field dt-skeleton"
                            style={{ width: '100%', height: '300px' }}
                        />
                    </Col>
                    <Col sm="12" md="4" className="mt-3">
                        <div
                            className="dt-field dt-skeleton"
                            style={{ width: '100%', height: '300px' }}
                        />
                    </Col>
                    <Col sm="12" md="4" className="mt-3">
                        <div
                            className="dt-field dt-skeleton"
                            style={{ width: '100%', height: '300px' }}
                        />
                    </Col>
                </Row>
            )
        }

        return (
            <>
                <Row className="align-items-stretch">
                    <Col lg={8} className="d-flex">
                        <div className="w-100 d-flex">
                            <LineChart
                                title="Count of Assets"
                                data={assetsByDateList}
                                height={360}
                            />
                        </div>
                    </Col>

                    <Col lg={4} className="d-flex flex-column">
                        {totalWidget && <Widget {...totalWidget} />}

                        <Card className="card_box_Shadow flex-grow-1">
                            <CardBody className="d-flex flex-column h-100">
                                <div className="mb-3">
                                    <CardTitle className="mb-0">
                                        {t("Assets Assigned to Me")}
                                    </CardTitle>
                                </div>
                                <div className="text-center flex-grow-1 d-flex align-items-center justify-content-center">
                                    <Knob
                                        value={assignedToMe?.percent}
                                        width={220}
                                        height={220}
                                        fgColor="#5664d2"
                                        lineCap="butt"
                                        readOnly={true}
                                        thickness={0.2}
                                    />
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                <Row>
                    <Col xl={4} lg={4} md={12} sm={12}>
                        <DonutChart title={t('My Hardware')} categories={myHardware} />
                    </Col>
                    <Col xl={4} lg={4} md={12} sm={12}>
                        <DonutChart title={t('My Software')} categories={mySoftware} />
                    </Col>
                    <Col xl={4} lg={4} md={12} sm={12}>
                        <DonutChart title={t('My Tasks')} categories={myTasks} />
                    </Col>
                </Row>
            </>
        )
    }

    return (
        <>
            <Row className="mb-4 mt-4">
                <Col className="d-flex justify-content-end align-items-start">
                    <Filtering onChange={handleFilterOnChange} />
                </Col>
            </Row>

            {renderBody()}
        </>
    );
}

export default withNamespaces()(TabProcessingActivityContent);
